// frontend/src/components/ExchangePreferenceRow.js
import React from 'react';
import { useDroppable, useDraggable } from '@dnd-kit/core';
import { Trash2 } from 'lucide-react';

const DroppableCell = ({ id, children, onRemove, isDuplicate }) => {
  const { setNodeRef } = useDroppable({ id });
  const handleContextMenu = (e) => {
    e.preventDefault();
    onRemove();
  };
  return (
    <div
      ref={setNodeRef}
      onContextMenu={handleContextMenu}
      className={`exchange-droppable-cell ${isDuplicate ? 'duplicate' : ''}`}
    >
      {children}
    </div>
  );
};

const DraggableItem = ({ id, data, children, isDuplicate }) => {
  const { attributes, listeners, setNodeRef, transform } = useDraggable({
    id,
    data: { current: { value: data } },
  });
  const style = transform
    ? { transform: `translate3d(${transform.x}px, ${transform.y}px, 0)` }
    : undefined;
  return (
    <div
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...listeners}
      className={`exchange-draggable-item ${isDuplicate ? 'duplicate' : ''}`}
    >
      {children}
    </div>
  );
};

const ExchangePreferenceRow = ({
  index,
  value,
  currentAssignment,
  onChange,
  onRemove,
  termOptions = [1, 2, 3, 4, 5, 6, 7],
  candidateDuplicate = false,
  isFirstRow = false,
}) => {
  const handleSourceChange = (e) => {
    onChange({
      ...value,
      sourceTerm: parseInt(e.target.value, 10),
      // 固定3件の補完候補にリセット
      complementCandidates: [null, null, null],
    });
  };

  const handleTargetChange = (e) => {
    onChange({
      ...value,
      targetTerm: parseInt(e.target.value, 10),
      complementCandidates: [null, null, null],
    });
  };

  const handleAccommodationToggle = (e) => {
    onChange({ ...value, accommodation: e.target.checked });
  };

  const handleCandidateRemove = () => {
    onChange({ ...value, candidate: null });
  };

  // 指定インデックスの補完候補を null に置換
  const handleComplementCandidateRemove = (idx) => {
    const newCandidates = (value.complementCandidates || [null, null, null]).map((item, i) =>
      i === idx ? null : item
    );
    onChange({ ...value, complementCandidates: newCandidates });
  };

  // 補完候補セルのレンダリング（常に 3 件固定）
  const renderComplementCandidateCell = (candidate, idx) => {
    const handleRemove = () => handleComplementCandidateRemove(idx);
    const labels = ['第1希望', '第2希望', '第3希望'];
    return (
      <div key={`complement-${idx}`} className="complement-candidate-item">
        <DroppableCell
          id={`exchange-${index}-complement-${idx}`}
          onRemove={handleRemove}
          isDuplicate={candidateDuplicate}
        >
          {candidate ? (
            <DraggableItem
              id={`exchange-${index}-complement-${idx}-drag`}
              data={candidate}
              isDuplicate={candidateDuplicate}
            >
              {candidate.display_name}
            </DraggableItem>
          ) : (
            <div className="exchange-placeholder">{labels[idx]}をドロップ</div>
          )}
        </DroppableCell>
      </div>
    );
  };

  return (
    <tr className="exchange-row" data-tour={isFirstRow ? 'exchange-row' : undefined}>
      <td className="exchange-current-assignment-cell" data-tour={isFirstRow ? 'exchange-current-assignment' : undefined}>
        <div className="assignment-text">
          <span className="hospital">
            {currentAssignment ? currentAssignment.institution_name : '未割り当て'}
          </span>
          {currentAssignment && currentAssignment.department_name && (
            <span className="department">{currentAssignment.department_name}</span>
          )}
        </div>
      </td>
      <td data-tour={isFirstRow ? 'exchange-source-term' : undefined}>
        <select value={value.sourceTerm} onChange={handleSourceChange} className="styled-select">
          {termOptions.map((term) => (
            <option key={`src-${term}`} value={term}>
              第{term}ターム
            </option>
          ))}
        </select>
      </td>
      <td data-tour={isFirstRow ? 'exchange-target-term' : undefined}>
        <select value={value.targetTerm} onChange={handleTargetChange} className="styled-select">
          {termOptions.map((term) => (
            <option key={`tgt-${term}`} value={term}>
              第{term}ターム
            </option>
          ))}
        </select>
      </td>
      <td data-tour={isFirstRow ? 'exchange-candidate' : undefined}>
        <DroppableCell
          id={`exchange-${index}-candidate`}
          onRemove={handleCandidateRemove}
          isDuplicate={candidateDuplicate}
        >
          {value.candidate ? (
            <DraggableItem
              id={`exchange-${index}-candidate-drag`}
              data={value.candidate}
              isDuplicate={candidateDuplicate}
            >
              {value.candidate.display_name}
            </DraggableItem>
          ) : (
            <div className="exchange-placeholder">候補をドロップ</div>
          )}
        </DroppableCell>
      </td>
      <td data-tour={isFirstRow ? 'exchange-complement' : undefined}>
        {value.sourceTerm !== value.targetTerm ? (
          <div className="complement-candidates-wrapper">
            {(value.complementCandidates || [null, null, null]).map((candidate, idx) =>
              renderComplementCandidateCell(candidate, idx)
            )}
          </div>
        ) : (
          <div className="exchange-placeholder" style={{ padding: '0.5rem' }}>不要</div>
        )}
      </td>
      <td data-tour={isFirstRow ? 'exchange-accommodation' : undefined}>
        <label className="exchange-accommodation-label">
          <input
            type="checkbox"
            checked={value.accommodation || false}
            onChange={handleAccommodationToggle}
          />
          希望する
        </label>
      </td>
      <td data-tour={isFirstRow ? 'exchange-remove' : undefined}>
        <button onClick={onRemove} className="exchange-remove-button" title="削除">
          <Trash2 size={16} />
        </button>
      </td>
    </tr>
  );
};

export default ExchangePreferenceRow;
